import React, { useState, useCallback, useEffect } from 'react';
import { X, Save, Music, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tag Input Component
function TagInput({ tags, onChange, placeholder }) {
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const newTag = inputValue.trim();
    if (newTag && !tags.includes(newTag)) {
      onChange([...tags, newTag]);
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-1.5 p-2 bg-white border border-border rounded-lg min-h-[42px] focus-within:border-primary transition-colors">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:bg-primary/20 rounded p-0.5 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm py-1 text-gray-900 placeholder-gray-400"
          placeholder={tags.length === 0 ? placeholder : 'Add more...'}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-1">Press Enter or comma to add</p>
    </div>
  );
}

export default function TrackEditModal({ track, token, onClose, onSave }) {
  const meta = track.final_metadata || {};
  const [form, setForm] = useState({
    title: meta.title || '',
    artist: meta.artist || '',
    description: meta.description || '',
    lyrics: track.lyrics || '',
    tempoBPM: meta.tempoBPM ? String(meta.tempoBPM) : '',
    key: meta.key || '',
    energyLevel: meta.energyLevel || '',
    energyDynamics: meta.energyDynamics || '',
    vocals: meta.vocals || '',
    timeSignature: meta.timeSignature || '',
    musicalEra: meta.musicalEra || '',
    estimatedProductionYear: meta.estimatedProductionYear ? String(meta.estimatedProductionYear) : '',
    aiGenerated: !!meta.aiGenerated,
    genre: meta.genre || [],
    subgenres: meta.subgenres || [],
    moodTags: meta.moodTags || [],
    moodAdvancedTags: meta.moodAdvancedTags || [],
    instrumentTags: meta.instrumentTags || [],
    sceneSuggestions: meta.sceneSuggestions || [],
    emotion: meta.emotion || '',
    emotionalDynamics: meta.emotionalDynamics || '',
    similarArtists: meta.similarArtists || [],
    characterTags: meta.characterTags || [],
    movementTags: meta.movementTags || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Heavy fields (lyrics, raw_metadata) are excluded from the list endpoint for performance.
  // Fetch the full track on open so the lyrics field pre-fills correctly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/tracks/${track.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const full = await res.json();
          if (full.lyrics !== undefined && full.lyrics !== null) {
            setForm(prev => ({ ...prev, lyrics: full.lyrics }));
          }
        }
      } catch { /* non-fatal; lyrics edit still works, just starts empty */ }
    })();
    return () => { cancelled = true; };
  }, [track.id, token]);

  const handleChange = useCallback((fieldName, value) => {
    setForm(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const updatedMetadata = {
      title: form.title,
      artist: form.artist,
      description: form.description,
      lyrics: form.lyrics,
      tempoBPM: form.tempoBPM ? parseFloat(form.tempoBPM) : null,
      key: form.key,
      energyLevel: form.energyLevel,
      energyDynamics: form.energyDynamics,
      vocals: form.vocals,
      timeSignature: form.timeSignature || null,
      musicalEra: form.musicalEra || null,
      estimatedProductionYear: form.estimatedProductionYear ? parseInt(form.estimatedProductionYear, 10) : null,
      aiGenerated: !!form.aiGenerated,
      genre: form.genre,
      subgenres: form.subgenres,
      moodTags: form.moodTags,
      moodAdvancedTags: form.moodAdvancedTags,
      instrumentTags: form.instrumentTags,
      sceneSuggestions: form.sceneSuggestions,
      emotion: form.emotion,
      emotionalDynamics: form.emotionalDynamics,
      similarArtists: form.similarArtists,
      characterTags: form.characterTags,
      movementTags: form.movementTags
    };

    try {
      const response = await fetch(`${API_URL}/api/tracks/${track.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updatedMetadata)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Update failed');
      }

      onSave();
    } catch (err) {
      setError(err.message);
    }

    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" data-testid="track-edit-modal">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Edit Track Metadata</h2>
              <p className="text-sm text-muted-foreground">{track.filename}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-black/10 transition-colors"
            data-testid="close-edit-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]" data-testid="edit-form">
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Basic Info</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Track title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Artist</label>
                  <input
                    type="text"
                    value={form.artist}
                    onChange={(e) => handleChange('artist', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Artist name"
                  />
                </div>
              </div>
            </div>

            {/* Sync Description */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Sync Description</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors resize-none text-sm text-gray-900 placeholder-gray-400"
                  placeholder="Sync description..."
                />
              </div>
            </div>

            {/* Lyrics */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Lyrics</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Transcribed Lyrics</label>
                <textarea
                  value={form.lyrics}
                  onChange={(e) => handleChange('lyrics', e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors resize-y text-sm text-gray-900 placeholder-gray-400 font-mono"
                  placeholder="Lyrics will appear here after transcription..."
                  data-testid="lyrics-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Edit lyrics to correct transcription errors</p>
              </div>
            </div>

            {/* Audio Properties */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Audio Properties</h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Tempo (BPM)</label>
                  <input
                    type="number"
                    value={form.tempoBPM}
                    onChange={(e) => handleChange('tempoBPM', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="120"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Key</label>
                  <input
                    type="text"
                    value={form.key}
                    onChange={(e) => handleChange('key', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="C Major"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Energy Level</label>
                  <input
                    type="text"
                    value={form.energyLevel}
                    onChange={(e) => handleChange('energyLevel', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="High"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Vocals</label>
                  <input
                    type="text"
                    value={form.vocals}
                    onChange={(e) => handleChange('vocals', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="yes/no"
                  />
                </div>
              </div>
            </div>

            {/* Era & Compliance — new fields from Cyanite Model Outputs */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Era &amp; Compliance</h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Time Signature</label>
                  <input
                    type="text"
                    value={form.timeSignature}
                    onChange={(e) => handleChange('timeSignature', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="4/4, 3/4"
                    data-testid="edit-time-signature"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Musical Era</label>
                  <input
                    type="text"
                    value={form.musicalEra}
                    onChange={(e) => handleChange('musicalEra', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="early2010s, late1990s"
                    data-testid="edit-musical-era"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Production Year</label>
                  <input
                    type="number"
                    value={form.estimatedProductionYear}
                    onChange={(e) => handleChange('estimatedProductionYear', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="2018"
                    data-testid="edit-production-year"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">AI-Generated</label>
                  <button
                    type="button"
                    onClick={() => handleChange('aiGenerated', !form.aiGenerated)}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      form.aiGenerated
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-gray-700 border-border hover:border-primary'
                    }`}
                    data-testid="edit-ai-generated"
                  >
                    {form.aiGenerated ? 'Yes — AI music' : 'No — human-made'}
                  </button>
                </div>
              </div>
            </div>

            {/* Energy & Emotion */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Energy & Emotion</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Energy Dynamics</label>
                  <input
                    type="text"
                    value={form.energyDynamics}
                    onChange={(e) => handleChange('energyDynamics', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Building, Steady"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Emotion</label>
                  <input
                    type="text"
                    value={form.emotion}
                    onChange={(e) => handleChange('emotion', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Happy, Melancholic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Emotional Dynamics</label>
                  <input
                    type="text"
                    value={form.emotionalDynamics}
                    onChange={(e) => handleChange('emotionalDynamics', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg focus:border-primary transition-colors text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Building, Drops"
                  />
                </div>
              </div>
            </div>

            {/* Genre & Style - Tag Inputs */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Genre & Style</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Genres</label>
                  <TagInput
                    tags={form.genre}
                    onChange={(tags) => handleChange('genre', tags)}
                    placeholder="Add genre..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Subgenres</label>
                  <TagInput
                    tags={form.subgenres}
                    onChange={(tags) => handleChange('subgenres', tags)}
                    placeholder="Add subgenre..."
                  />
                </div>
              </div>
            </div>

            {/* Mood Tags */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Mood Tags</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Mood Tags</label>
                  <TagInput
                    tags={form.moodTags}
                    onChange={(tags) => handleChange('moodTags', tags)}
                    placeholder="Add mood..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Advanced Mood Tags</label>
                  <TagInput
                    tags={form.moodAdvancedTags}
                    onChange={(tags) => handleChange('moodAdvancedTags', tags)}
                    placeholder="Add advanced mood..."
                  />
                </div>
              </div>
            </div>

            {/* Instruments */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Instruments</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Instruments</label>
                <TagInput
                  tags={form.instrumentTags}
                  onChange={(tags) => handleChange('instrumentTags', tags)}
                  placeholder="Add instrument..."
                />
              </div>
            </div>

            {/* Scene & Character */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Scene & Character</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Scene Suggestions</label>
                  <TagInput
                    tags={form.sceneSuggestions}
                    onChange={(tags) => handleChange('sceneSuggestions', tags)}
                    placeholder="Add scene..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Character Tags</label>
                  <TagInput
                    tags={form.characterTags}
                    onChange={(tags) => handleChange('characterTags', tags)}
                    placeholder="Add character..."
                  />
                </div>
              </div>
            </div>

            {/* Movement & Similar Artists */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Movement & Similar Artists</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Movement Tags</label>
                  <TagInput
                    tags={form.movementTags}
                    onChange={(tags) => handleChange('movementTags', tags)}
                    placeholder="Add movement..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Similar Artists</label>
                  <TagInput
                    tags={form.similarArtists}
                    onChange={(tags) => handleChange('similarArtists', tags)}
                    placeholder="Add artist..."
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm" data-testid="edit-error">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 border border-border rounded-xl hover:bg-black/5 transition-colors text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl btn-lift disabled:opacity-50"
              data-testid="save-edit"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
